import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BookmarkPlus, Check, Copy, Loader2, Trash2 } from 'lucide-react'
import { AddToChatIcon } from './AddToChatIcon'
import { AgentNodeToolbar, type ToolbarAction } from './AgentNodeToolbar'
import { useNodeActions } from './useNodeActions'
import { useDuplicateMenuItems } from './useDuplicateMenuItems'
import { useSaveToLibrary } from './useSaveToLibrary'

interface UploadMediaNodeToolbarProps {
  nodeId: string
  selected?: boolean
  dragging?: boolean
  /**
   * Media-type-specific actions, appended before the destructive group.
   *
   * This toolbar is shared by the image, audio and video upload nodes, so an
   * action that only makes sense for one of them (RFC-1115's "Annotate", which
   * needs an image path the audio node does not have) is owned by that node and
   * passed in rather than being conditionally built here.
   */
  extraActions?: ToolbarAction[]
  /** Displayed node name (label or its fallback), used to name duplicates. */
  originalName: string
}

export function UploadMediaNodeToolbar({
  nodeId,
  selected,
  dragging,
  extraActions,
  originalName,
}: UploadMediaNodeToolbarProps) {
  const { t } = useTranslation('canvas')
  const {
    handleAddToChat,
    handleDuplicate,
    handleDelete,
  } = useNodeActions({ nodeId })
  const duplicateMenuItems = useDuplicateMenuItems(handleDuplicate, originalName)
  const { saveState, handleSaveToLibrary, saveLabel } = useSaveToLibrary({ nodeId, selected })

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'add-to-chat',
        label: t('uploadMediaNodeToolbar.addToChat'),
        icon: <AddToChatIcon className="h-4 w-4" />,
        onClick: handleAddToChat,
      },
      {
        key: 'save-to-library',
        label: saveLabel,
        icon: saveState === 'loading'
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : saveState === 'success'
            ? <Check className="h-4 w-4 text-green-400" />
            : <BookmarkPlus className="h-4 w-4" />,
        onClick: handleSaveToLibrary,
        disabled: saveState !== 'idle',
      },
      ...(extraActions ?? []),
      {
        key: 'duplicate',
        label: t('uploadMediaNodeToolbar.duplicate'),
        icon: <Copy className="h-4 w-4" />,
        menuItems: duplicateMenuItems,
        group: true,
      },
      {
        key: 'remove',
        label: t('uploadMediaNodeToolbar.remove'),
        icon: <Trash2 className="h-4 w-4" />,
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [t, handleAddToChat, handleSaveToLibrary, saveState, saveLabel, duplicateMenuItems, handleDelete, extraActions],
  )

  return (
    <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />
  )
}
